import { useState, useEffect } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import MagicLinkGenerator from "components/TenantMagicLink/MagicLinkGenerator";

// Icons
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";

import { useAuth } from "contexts/AuthContext";
import { tenantService } from "services/firestoreService";

// Legacy dummy data - replaced by Firestore
const _LEGACY_TENANTS = [
  {
    id: "T-001",
    name: "Sarah Johnson",
    property: "Sunset Gardens",
    unit: "112",
    lease_start: "05/01/2024",
    lease_end: "04/30/2025",
    rent: "$1,450",
    status: "active",
    phone: "(555) 123-4567",
    email: "sarah.j@example.com",
  },
  {
    id: "T-002",
    name: "Miguel Rodriguez",
    property: "Oakwood Heights",
    unit: "304",
    lease_start: "02/15/2024",
    lease_end: "02/14/2025",
    rent: "$1,850",
    status: "active",
    phone: "(555) 234-5678",
    email: "mrodriguez@example.com",
  },
  {
    id: "T-003",
    name: "James Wilson",
    property: "Riverside Apartments",
    unit: "207",
    lease_start: "11/01/2023",
    lease_end: "10/31/2024",
    rent: "$1,250",
    status: "active",
    phone: "(555) 345-6789",
    email: "jwilson@example.com",
  },
  {
    id: "T-004",
    name: "Emma Chen",
    property: "Sunset Gardens",
    unit: "224",
    lease_start: "01/15/2024",
    lease_end: "01/14/2025",
    rent: "$1,375",
    status: "active",
    phone: "(555) 456-7890",
    email: "echen@example.com",
  },
  {
    id: "T-005",
    name: "David Kim",
    property: "Oakwood Heights",
    unit: "118",
    lease_start: "03/01/2024",
    lease_end: "08/31/2024",
    rent: "$1,650",
    status: "notice_given",
    phone: "(555) 567-8901",
    email: "dkim@example.com",
  },
  {
    id: "T-006",
    name: "Lisa Brown",
    property: "Riverside Apartments",
    unit: "315",
    lease_start: "06/15/2023",
    lease_end: "06/14/2024",
    rent: "$1,150",
    status: "renewal_pending",
    phone: "(555) 678-9012",
    email: "lbrown@example.com",
  },
  {
    id: "T-007",
    name: "Marcus Johnson",
    property: "Sunset Gardens",
    unit: "145",
    lease_start: "04/01/2024",
    lease_end: "03/31/2025",
    rent: "$1,550",
    status: "active",
    phone: "(555) 789-0123",
    email: "mjohnson@example.com",
  },
  {
    id: "T-008",
    name: "Sophia Patel",
    property: "Oakwood Heights",
    unit: "207",
    lease_start: "02/01/2024",
    lease_end: "01/31/2025",
    rent: "$1,750",
    status: "active",
    phone: "(555) 890-1234",
    email: "spatel@example.com",
  },
];

// Properties list
const PROPERTIES = [
  "All Properties",
  "Sunset Gardens",
  "Oakwood Heights",
  "Riverside Apartments",
];

function TenantDirectory() {
  const { user } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    if (!user) return;
    const fetchTenants = async () => {
      try {
        const data = await tenantService.getAll(user.uid);
        setTenants(data);
      } catch (err) {
        console.error("Failed to fetch tenants:", err);
      } finally {
        setLoadingData(false);
      }
    };
    fetchTenants();
  }, [user]);
  const [searchTerm, setSearchTerm] = useState("");
  const [propertyFilter, setPropertyFilter] = useState("All Properties");
  const [statusFilter, setStatusFilter] = useState("all");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);

  // Filter the tenants based on search term and filters
  const filteredTenants = tenants.filter((tenant) => {
    // Search term filter
    const matchesSearch =
      tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tenant.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tenant.id.toLowerCase().includes(searchTerm.toLowerCase());

    // Property filter
    const matchesProperty =
      propertyFilter === "All Properties" || tenant.property === propertyFilter;

    // Status filter
    const matchesStatus =
      statusFilter === "all" || tenant.status === statusFilter;

    return matchesSearch && matchesProperty && matchesStatus;
  });

  const handleViewTenant = (tenant) => {
    setSelectedTenant(tenant);
    setDrawerOpen(true);
  };

  const handleEditTenant = (tenant) => {
    setSelectedTenant(tenant);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedTenant(null);
  };

  // Render status chip with appropriate color
  const renderStatusChip = (status) => {
    let color = "default";
    let label = "Unknown";

    switch (status) {
      case "active":
        color = "success";
        label = "Active";
        break;
      case "notice_given":
        color = "warning";
        label = "Notice Given";
        break;
      case "renewal_pending":
        color = "info";
        label = "Renewal Pending";
        break;
      case "past_due":
        color = "error";
        label = "Past Due";
        break;
      default:
        break;
    }

    return <Chip label={label} color={color} size="small" />;
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <Card sx={{ marginBottom: 3 }}>
          <ArgonBox p={3}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={6}>
                <ArgonTypography variant="h4" fontWeight="medium">
                  Tenant Directory
                </ArgonTypography>
                <ArgonTypography variant="body2" color="text">
                  Manage tenant information across all properties
                </ArgonTypography>
              </Grid>
              <Grid item xs={12} md={6} sx={{ textAlign: "right" }}>
                <ArgonButton
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                >
                  Add New Tenant
                </ArgonButton>
              </Grid>
            </Grid>
          </ArgonBox>
        </Card>

        <Card>
          <ArgonBox p={3}>
            {/* Search and Filters */}
            <Grid container spacing={2} sx={{ marginBottom: 3 }}>
              <Grid item xs={12} md={4}>
                <ArgonInput
                  placeholder="Search tenants..."
                  icon={{ component: <SearchIcon />, direction: "left" }}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="property-filter-label">Property</InputLabel>
                  <Select
                    labelId="property-filter-label"
                    value={propertyFilter}
                    label="Property"
                    onChange={(e) => setPropertyFilter(e.target.value)}
                  >
                    {PROPERTIES.map((property) => (
                      <MenuItem key={property} value={property}>
                        {property}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="status-filter-label">Status</InputLabel>
                  <Select
                    labelId="status-filter-label"
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Statuses</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="notice_given">Notice Given</MenuItem>
                    <MenuItem value="renewal_pending">Renewal Pending</MenuItem>
                    <MenuItem value="past_due">Past Due</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <ArgonButton
                  variant="outlined"
                  color="primary"
                  fullWidth
                  startIcon={<FilterListIcon />}
                >
                  More Filters
                </ArgonButton>
              </Grid>
            </Grid>

            {/* Tenant Table */}
            <TableContainer>
              <Table sx={{ minWidth: 650 }} aria-label="tenant directory table">
                <TableHead>
                  <TableRow>
                    <TableCell>Tenant ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Property</TableCell>
                    <TableCell>Unit</TableCell>
                    <TableCell>Lease End</TableCell>
                    <TableCell>Monthly Rent</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTenants.map((tenant) => (
                    <TableRow
                      key={tenant.id}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">
                        {tenant.id}
                      </TableCell>
                      <TableCell>{tenant.name}</TableCell>
                      <TableCell>{tenant.property}</TableCell>
                      <TableCell>{tenant.unit}</TableCell>
                      <TableCell>{tenant.lease_end}</TableCell>
                      <TableCell>{tenant.rent}</TableCell>
                      <TableCell>{renderStatusChip(tenant.status)}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleViewTenant(tenant)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleEditTenant(tenant)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 3,
              }}
            >
              <ArgonTypography variant="body2">
                Showing {filteredTenants.length} of {tenants.length}{" "}
                tenants
              </ArgonTypography>
              <Pagination count={1} color="primary" />
            </Box>
          </ArgonBox>
        </Card>
      </ArgonBox>

      {/* Tenant Details Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        sx={{ "& .MuiDrawer-paper": { width: { xs: "100%", sm: 450 } } }}
      >
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <ArgonTypography variant="h5">
              {selectedTenant ? "Tenant Details" : "Add New Tenant"}
            </ArgonTypography>
            <IconButton onClick={handleCloseDrawer}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 3 }} />

          {selectedTenant && (
            <Stack spacing={3}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Tenant ID"
                    value={selectedTenant.id}
                    fullWidth
                    disabled
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Status"
                    value={selectedTenant.status.replace("_", " ")}
                    fullWidth
                    disabled
                    size="small"
                  />
                </Grid>
              </Grid>

              <TextField
                label="Full Name"
                value={selectedTenant.name}
                fullWidth
                size="small"
              />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Phone"
                    value={selectedTenant.phone}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Email"
                    value={selectedTenant.email}
                    fullWidth
                    size="small"
                  />
                </Grid>
              </Grid>

              <Divider />
              <ArgonTypography variant="subtitle2">
                Lease Information
              </ArgonTypography>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Property"
                    value={selectedTenant.property}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Unit #"
                    value={selectedTenant.unit}
                    fullWidth
                    size="small"
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Lease Start"
                    value={selectedTenant.lease_start}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Lease End"
                    value={selectedTenant.lease_end}
                    fullWidth
                    size="small"
                  />
                </Grid>
              </Grid>

              <TextField
                label="Monthly Rent"
                value={selectedTenant.rent}
                fullWidth
                size="small"
              />

              <Divider />

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <ArgonButton
                  variant="outlined"
                  color="secondary"
                  onClick={handleCloseDrawer}
                >
                  Cancel
                </ArgonButton>
                <ArgonButton variant="contained" color="primary">
                  Save Changes
                </ArgonButton>
              </Stack>
            </Stack>
          )}
        </Box>
      </Drawer>

      <Footer />
    </DashboardLayout>
  );
}

export default TenantDirectory;